import { formatPhone, phoneFromDigits } from "@rhema/client";
import { Button, EmptyState, PhoneIcon } from "@rhema/ui";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { chatWithNumber } from "../people/byNumber";
import { InviteSheet } from "../people/InviteSheet";
import { useFeatures } from "../session/features";
import { useApi, useClient, useMe } from "../session/session";

/** A /chat/<digits> link on a server that does not find people by number yet. */
function NumbersOff() {
  return (
    <EmptyState
      icon={<PhoneIcon size={26} />}
      title="Phone numbers are not on this server yet"
      className="flex-1"
    >
      This link opens a chat with a phone number. This Rhema server does not find people by phone number yet,
      so ask them for the email address they use for Rhema.
    </EmptyState>
  );
}

/** Opens a chat link once the server says whether it finds people by number. */
export function ChatByNumber({ digits }: { digits: string }) {
  const features = useFeatures();
  if (!features) return <EmptyState title="Opening the chat" className="flex-1" />;
  if (!features.phone) return <NumbersOff />;
  return <ChatWithNumber digits={digits} />;
}

type Found = { kind: "loading" } | { kind: "missing" } | { kind: "failed"; message: string };

/**
 * Opens https://app.rhema.siliconblast.com/chat/<digits>, from a link or a scanned QR code. It goes
 * straight to the chat with that number, or offers to invite them when they are not on Rhema.
 */
function ChatWithNumber({ digits }: { digits: string }) {
  const api = useApi();
  const client = useClient();
  const me = useMe();
  const navigate = useNavigate();
  const phone = phoneFromDigits(digits);
  const [found, setFound] = useState<Found>({ kind: "loading" });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (!phone) return;
    let live = true;
    chatWithNumber(api, client, phone, me.phone)
      .then((result) => {
        if (!live) return;
        if (result.kind === "chat") navigate(`/chats/${result.chat.id}`, { replace: true });
        else setFound({ kind: "missing" });
      })
      .catch((err: Error) => live && setFound({ kind: "failed", message: err.message }));
    return () => {
      live = false;
    };
  }, [api, client, phone, me.phone, navigate]);

  if (!phone) return <Navigate to="/chats" replace />;
  if (found.kind === "loading") return <EmptyState title="Opening the chat" className="flex-1" />;
  if (found.kind === "failed") {
    return (
      <EmptyState title="The chat did not open" className="flex-1">
        {found.message}
      </EmptyState>
    );
  }
  return (
    <div className="flex flex-1 flex-col">
      <EmptyState
        icon={<PhoneIcon size={26} />}
        title={`${formatPhone(phone)} is not on Rhema`}
        className="flex-1"
        action={<Button onClick={() => setInviting(true)}>Invite to Rhema</Button>}
      >
        Send them an invitation. Once they join, you can message them here.
      </EmptyState>
      <InviteSheet open={inviting} onClose={() => setInviting(false)} phone={phone} />
    </div>
  );
}
